/**
 * VerticalLinePrimitive — draws vertical lines at specified time positions.
 * Used to mark session boundaries (09:15 IST NSE / 09:00 MCX / 05:30 DELTA).
 *
 * Unlike createPriceLine (horizontal), lightweight-charts has no built-in
 * vertical line, so we use a series primitive.
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export type VLineStyle = 'solid' | 'dashed' | 'dotted';

export interface VerticalLineOptions {
  color: string;      // CSS rgba — opacity baked in
  width?: number;     // default 1
  style?: VLineStyle; // default 'dashed'
}

function dashPattern(style: VLineStyle, width: number): number[] {
  switch (style) {
    case 'solid':  return [];
    case 'dotted': return [width, width * 2];
    case 'dashed':
    default:       return [width * 4, width * 3];
  }
}

class VLineRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly times: number[],
    private readonly opts: VerticalLineOptions,
    private readonly chart: IChartApi,
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    if (!this.times.length) return;
    const ts = this.chart.timeScale();

    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      const w = Math.max(1, (this.opts.width || 1) * Math.min(hpr, vpr));
      ctx.lineWidth = w;
      ctx.strokeStyle = this.opts.color;
      const dash = dashPattern(this.opts.style || 'dashed', w);
      ctx.setLineDash(dash);

      for (const t of this.times) {
        const x = ts.timeToCoordinate(t as Time);
        if (x == null) continue;
        const px = Number(x) * hpr;
        ctx.beginPath();
        ctx.moveTo(px, 0);
        ctx.lineTo(px, bitmapSize.height);
        ctx.stroke();
      }
      ctx.restore();
    });
  }
}

class VLinePaneView implements IPrimitivePaneView {
  constructor(private readonly parent: VerticalLinePrimitive) {}
  zOrder() { return 'bottom' as const; } // behind candles
  renderer(): IPrimitivePaneRenderer | null {
    const chart = this.parent.chart;
    if (!chart) return null;
    return new VLineRenderer(this.parent.times, this.parent.opts, chart);
  }
}

export class VerticalLinePrimitive implements ISeriesPrimitive<Time> {
  times: number[] = [];
  opts: VerticalLineOptions;
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: VLinePaneView[];

  constructor(times: number[], opts: VerticalLineOptions) {
    this.times = times;
    this.opts = opts;
    this._paneViews = [new VLinePaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void {
    this.chart = null;
    this.series = null;
  }

  updateAllViews(): void { /* renderer pulls fresh from this.times */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(times: number[]): void {
    this.times = times;
    this.chart?.applyOptions({});
  }
}
